import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from "components/GlobalContext";
import { Accordion, Button, Card, Col, Form, Row } from "react-bootstrap";
import IEquipment, { IEquipmentCreateOrUpdate, IEquipmentInitResult } from "models/sql/IEquipment";
import EquipmentService from "services/EquipmentService";
import moment from 'moment';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import IProduct, { IProductFilters } from "models/sql/IProduct";
import ProductService from "services/ProductService";
import IFireStation from "models/sql/IFireStation";
import FireStationService from "services/FireStationService";
import IDealer, { IDealerFilters } from "models/sql/IDealer";
import DealerService from "services/DealerService";
import IFireDepartment, { IFireDepartmentFilters } from "models/sql/IFireDepartment";
import FireDepartmentService from "services/FireDepartmentService";
import { addNewLogEntry, generateLog, getEnvironment } from "utilities/MiscUtilities";
import ISalesRep, { ISalesRepFilters } from "models/sql/ISalesRep";
import SalesRepService from "services/SalesRepService";
import CountryChooser from "components/CountryChooser";
import MarkdownEditor from "components/MarkdownEditor";
import MiscService from "services/MiscService";
import { useBeforeUnload } from "utilities/UIUtilities";
import DPSEntitySelector from "components/DPSEntitySelector";
import StateChooser from "components/StateChooser";
import DetailFooter from "components/DetailFooter";

interface IDemosDetailProps {
    baseData?: IEquipmentInitResult;
}

const DemosDetail = (props: IDemosDetailProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const { id }: any = useParams();
    const [product, setProduct] = useState<IProduct>();
    const [dealer, setDealer] = useState<IDealer>();
    const [salesRep, setSalesRep] = useState<ISalesRep>();
    const [fireStation, setFireStation] = useState<IFireStation>();
    const [fireDepartment, setFireDepartment] = useState<IFireDepartment>();
    const [equipment, setEquipment] = useState<IEquipment | null>({
        equipmentId: -1,
        serialNumber: "",
        dealerId: undefined,
        fireDepartmentId: undefined,
        extraData: "",
        notes: "",
        parentEquipmentId: undefined,
        productId: undefined,
        registrationDate: undefined,
        startedService: undefined,
        stationId: undefined,
        registrationEmail: "",
        registrationPostalCode: "",
        registrationName: "",
        demoRequestDate: undefined,
        demoApprovalDate: undefined,
        demoStartDate: undefined,
        demoEndDate: undefined,
        demoRequestEmail: "",
        demoRequestName: "",
        systemDataJson: "",
        location: "",
        building: "",
        region: "",
        country: "",
        city: "",
        departmentName: "",
        created: undefined,
        outOfServiceDate: undefined,
        repAssignDate: undefined,
        salesRepId: undefined,
        score: undefined,
        shippingNotes: "",
        stationNumber: "",
        address1: "",
        address2: "",
        state: "",
        postalCode: "",
        contactName: "",
        contactTitle: "",
        contactEmail: "",
        qaCompleteDate: undefined,
        receivedDate: undefined,
        shippedDate: undefined,
        demoDuration: undefined
    });
    const navigate = useNavigate();
    const [hasFormDataChanged, setHasFormDataChanged] = useState(false);
    const demoDurations = ["30", "60", "90", "120"];

    useBeforeUnload(
        hasFormDataChanged,
        "You have unsaved changes, please save them before leaving the page.");

    useEffect(
        () => {
            (async () => {
                if (id !== undefined) {
                    globalContext.setLoading(true);

                    const accessToken = await globalContext.getAccessTokenAsync();
                    const result = await EquipmentService.GetAsync(accessToken, id);

                    if (result.record) {
                        setEquipment(result.record);

                        if (result.record.productId !== null && result.record.productId !== undefined) {
                            const productResult = await ProductService.GetAsync(accessToken, result.record.productId);

                            if (productResult.record) {
                                setProduct(productResult.record);
                            }
                        }

                        if (result.record.stationId !== null && result.record.stationId !== undefined) {
                            const stationResult = await FireStationService.GetAsync(accessToken, result.record.stationId);

                            if (stationResult.record) {
                                setFireStation(stationResult.record);
                            }
                        }

                        if (result.record.fireDepartmentId !== null && result.record.fireDepartmentId !== undefined) {
                            const departmentResult = await FireDepartmentService.GetAsync(accessToken, result.record.fireDepartmentId);

                            if (departmentResult.record) {
                                setFireDepartment(departmentResult.record);
                            }
                        }

                        if (result.record.dealerId !== null && result.record.dealerId !== undefined) {
                            const dealerResult = await DealerService.GetAsync(accessToken, result.record.dealerId);

                            if (dealerResult.record) {
                                setDealer(dealerResult.record);
                            }
                        }

                        if (result.record.salesRepId !== null && result.record.salesRepId !== undefined) {
                            const salesRepResult = await SalesRepService.GetAsync(accessToken, result.record.salesRepId);

                            if (salesRepResult.record) {
                                setSalesRep(salesRepResult.record);
                            }
                        }
                    }
                    else {
                        setEquipment(null);
                        globalContext.showErrorMessage("Unable to load Demo");
                    }

                    globalContext.setLoading(false);
                }
            })();
        }, [id]);

    const deleteAsync = async () => {
        if (equipment && equipment.equipmentId && window.confirm("Are you SURE you want to delete this?")) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();
            await EquipmentService.DeleteAsync(accessToken, equipment.equipmentId);
            globalContext.setLoading(false);
            navigate("/Demos");
        }
    };

    const submitFormAsync = async (changePage = true, newEquipmentData: IEquipment | null = null) => {
        if (!newEquipmentData) {
            newEquipmentData = equipment;
        }
        else {
            setEquipment(newEquipmentData);
        }

        if (newEquipmentData) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();

            const data: IEquipmentCreateOrUpdate = {
                equipmentId: newEquipmentData.equipmentId,
                dealerId: newEquipmentData.dealerId,
                fireDepartmentId: newEquipmentData.fireDepartmentId,
                stationId: newEquipmentData.stationId,
                productId: newEquipmentData.productId,
                serialNumber: newEquipmentData.serialNumber,
                parentEquipmentId: newEquipmentData.parentEquipmentId,
                startedService: newEquipmentData.startedService || undefined,
                registrationDate: newEquipmentData.registrationDate || undefined,
                extraData: newEquipmentData.extraData,
                notes: newEquipmentData.notes,
                registrationEmail: newEquipmentData.registrationEmail,
                registrationName: newEquipmentData.registrationName,
                registrationPostalCode: newEquipmentData.registrationPostalCode,
                demoRequestDate: newEquipmentData.demoRequestDate || undefined,
                demoApprovalDate: newEquipmentData.demoApprovalDate || undefined,
                demoStartDate: newEquipmentData.demoStartDate || undefined,
                demoEndDate: newEquipmentData.demoEndDate || undefined,
                demoRequestName: newEquipmentData.demoRequestName,
                demoRequestEmail: newEquipmentData.demoRequestEmail,
                systemDataJson: newEquipmentData.systemDataJson,
                location: newEquipmentData.location,
                building: newEquipmentData.building,
                region: newEquipmentData.region,
                country: newEquipmentData.country,
                city: newEquipmentData.city,
                departmentName: newEquipmentData.departmentName,
                created: newEquipmentData.created,
                outOfServiceDate: newEquipmentData.outOfServiceDate || undefined,
                repAssignDate: newEquipmentData.repAssignDate || undefined,
                salesRepId: newEquipmentData.salesRepId,
                score: newEquipmentData.score,
                shippingNotes: newEquipmentData.shippingNotes,
                stationNumber: newEquipmentData.stationNumber,
                address1: newEquipmentData.address1,
                address2: newEquipmentData.address2,
                state: newEquipmentData.state,
                postalCode: newEquipmentData.postalCode,
                contactName: newEquipmentData.contactName,
                contactTitle: newEquipmentData.contactTitle,
                contactEmail: newEquipmentData.contactEmail,
                qaCompleteDate: newEquipmentData.qaCompleteDate,
                receivedDate: newEquipmentData.receivedDate,
                shippedDate: newEquipmentData.shippedDate,
                demoDuration: newEquipmentData.demoDuration
            };

            await EquipmentService.CreateOrUpdateAsync(accessToken, data);
            setHasFormDataChanged(false);
            globalContext.setLoading(false);

            if (data.equipmentId === -1) {
                navigate(-1);
            }
        }
    };

    if (equipment) {
        return (
            <>
                <Row>
                    <Col>
                        {
                            (equipment.systemDataJson ?? "").indexOf("sent to order entry") > -1 ?
                                <span className="ps-2" style={{ width: "100%", display: "block", textAlign: "center", backgroundColor: "#19875473", padding: "6px", fontWeight: "bold" }}><FontAwesomeIcon icon={faCheck as IconProp} /> Approved and sent to OE</span> :
                                <Button
                                    style={{ width: "100%" }}
                                    variant="success"
                                    title="Send Email to OE"
                                    className="quick-link"
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        globalContext.showModal({
                                            content:
                                                <>Press OK to confirm approving this demo and sending an email to OE for fulfillment.</>,
                                            title: "Send Email to OE",
                                            okCallback:
                                                async (data) => {
                                                    if (!equipment.contactEmail) {
                                                        window.alert("You must enter demo contact info (atleast the email) before sending this demo to OE.");
                                                        return;
                                                    }

                                                    if (!equipment.salesRep) {
                                                        window.alert("You must assign a sales rep before sending this demo to OE.");
                                                        return;
                                                    }

                                                    if (!equipment.product) {
                                                        window.alert("You must select a product before sending this demo to OE.");
                                                        return;
                                                    }

                                                    if (!equipment.fireDepartment && !equipment.departmentName) {
                                                        window.alert("You must select a department or enter a manual department name before sending this demo to OE.");
                                                        return;
                                                    }

                                                    const accessToken = await globalContext.getAccessTokenAsync();

                                                    var body1 = `Hello from TFT Health!<br/><br/>`;
                                                    body1 += `Please enter a no-charge order using the information below. Contact Brock Birky with any questions.<br/><br/>`;
                                                    body1 += `<ul><li><strong>Entity ID:</strong> ${equipment.dealer?.entityId ?? ""}</li>`;
                                                    body1 += `<li><strong>Dealer Name:</strong> ${equipment.dealer?.dealerName ?? ""}</li>`;
                                                    body1 += `<li><strong>Dealer Rep:</strong> ${equipment.salesRep?.fullName ?? ""}</li>`;
                                                    body1 += `<li><strong>Customer Name:</strong> ${equipment.fireDepartment?.fireDeptName || equipment.departmentName}</li>`;
                                                    body1 += `<li><strong>Attn. to:</strong> ${equipment.contactName}</li>`;
                                                    body1 += `<li><strong>Ship to Address:</strong> ${equipment.address1}${equipment.address2 ? " " + equipment.address2 : ""} ${equipment.city} ${equipment.state} ${equipment.postalCode}, ${equipment.country}</li>`;
                                                    body1 += `<li><strong>Product:</strong> ${equipment.product?.partNumber ?? ""} ${equipment.product?.name ?? ""}</li>`;
                                                    body1 += `<li><strong>Special Notes:</strong> ${equipment.shippingNotes}</li></ul><br/>`;
                                                    body1 += `Sincerely,<br/>`;
                                                    body1 += `The TFT Health Team`;

                                                    await MiscService.sendEmailRawAsync(
                                                        accessToken,
                                                        getEnvironment() === "prod" ? "sales@tft.com,kristyn.broach@tft.com,ricardo.gonzalez@tft.com" : "jgoergen83@gmail.com,erik.ward@tft.com",
                                                        "NoReply@TFTHealth.com",
                                                        "Health No-Charge Order Request",
                                                        body1);

                                                    var body2 = `Hello from TFT Health.<br/><br/>`;
                                                    body2 += `Great news! A product demo has been approved for ${equipment.fireDepartment?.fireDeptName || equipment.departmentName}. We are preparing to ship:<br/><br/>`;
                                                    body2 += `${equipment.product?.name}, Quantity 1<br/><br/>`;
                                                    body2 += `This demo has been coordinated by ${equipment.salesRep?.fullName} at ${equipment.dealer?.dealerName}. Please contact them for any questions or updates.<br/><br/>`;
                                                    body2 += `Sincerely,<br/>`;
                                                    body2 += `The TFT Health Team`;

                                                    await MiscService.sendEmailRawAsync(
                                                        accessToken,
                                                        getEnvironment() === "prod" ? `${equipment.contactEmail},${equipment.salesRep?.email}` : "jgoergen83@gmail.com,erik.ward@tft.com",
                                                        "NoReply@TFTHealth.com",
                                                        "TFT Health Demo Approved!",
                                                        body2);

                                                    equipment.demoApprovalDate = (new Date()).toISOString();

                                                    await submitFormAsync(
                                                        false,
                                                        addNewLogEntry(
                                                            (new Date()),
                                                            `Demo request approved and sent to order entry`,
                                                            globalContext.userEmail,
                                                            101,
                                                            equipment));

                                                    window.alert("A no-charge order request has been sent to OE.");
                                                },
                                            cancelCallback: () => { }
                                        });
                                    }}>
                                    <FontAwesomeIcon icon={faEnvelope as IconProp} /> Approve and Send to OE
                                </Button>
                        }
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Card className="mb-2 mt-2">
                            <Card.Body>
                                <Card.Title>
                                    {
                                        id !== undefined &&
                                        <strong>{equipment.product?.name} ({equipment.product?.partNumber})</strong>
                                    }
                                    {
                                        id === undefined &&
                                        <strong>New Demo</strong>
                                    }
                                </Card.Title>

                                <DPSEntitySelector<IProduct, IProductFilters>
                                    entityFilterSearchPropName={"names"}
                                    entityService={ProductService}
                                    entityTypeName="Product"
                                    defaultEntityFilter={{}}
                                    selectedEntityRef={product}
                                    generateEntityTitle={(entry) => entry.name}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/Products/Detail/${entry.productId}`);
                                        }
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setEquipment({ ...equipment, productId: newEntry?.productId || undefined });
                                        setProduct(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <DPSEntitySelector<IFireDepartment, IFireDepartmentFilters>
                                    entityFilterSearchPropName={"fireDeptNames"}
                                    entityService={FireDepartmentService}
                                    entityTypeName="Department"
                                    defaultEntityFilter={{}}
                                    selectedEntityRef={fireDepartment}
                                    generateEntityTitle={(entry) => `${entry.fireDeptName} ${entry.hqcity} ${entry.hqstate}, ${entry.hqzip}`}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/Departments/Detail/${entry.fireDepartmentId}`);
                                        }
                                    }}
                                    onClickAddNewHandler={() => {
                                        window.open(`/Departments/Detail/?defs={"fireDeptName":"${equipment.departmentName ?? ""}","hqstate":"","hqCountry":"${equipment.country ?? ""}"}`);
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setEquipment({ ...equipment, fireDepartmentId: newEntry?.fireDepartmentId || undefined });
                                        setFireDepartment(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />
                                {/*
                                <DPSEntitySelector<IFireStation, IFireStationFilters>
                                    entityFilterSearchPropName={"postalCodes"}
                                    entityService={FireStationService}
                                    entityTypeName="Station"
                                    defaultEntityFilter={{}}
                                    selectedEntityRef={fireStation}
                                    generateEntityTitle={(entry) => `${entry.name} (${entry.number})`}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/Stations/Detail/${entry.fireStationId}`);
                                        }
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setEquipment({ ...equipment, stationId: newEntry?.fireStationId || undefined });
                                        setFireStation(newEntry);
                                            setHasFormDataChanged(true);
                                    }}
                                />
                                */}
                                <DPSEntitySelector<IDealer, IDealerFilters>
                                    entityFilterSearchPropName={"dealerNames"}
                                    entityService={DealerService}
                                    entityTypeName="Dealer"
                                    defaultEntityFilter={{}}
                                    selectedEntityRef={dealer}
                                    generateEntityTitle={(entry) => entry.dealerName ?? ""}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/Dealers/Detail/${entry.dealerId}`);
                                        }
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setEquipment({ ...equipment, dealerId: newEntry?.dealerId || undefined });
                                        setDealer(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <DPSEntitySelector<ISalesRep, ISalesRepFilters>
                                    entityFilterSearchPropName={"names"}
                                    entityService={SalesRepService}
                                    entityTypeName="Rep"
                                    defaultEntityFilter={{ dealerIds: dealer ? [dealer.dealerId] : undefined }}
                                    selectedEntityRef={salesRep}
                                    generateEntityTitle={(entry) => entry?.fullName ?? ""}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/Reps/Detail/${entry.salesRepId}`);
                                        }
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setEquipment({ ...equipment, salesRepId: newEntry?.salesRepId || undefined });
                                        setSalesRep(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Serial Number</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, serialNumber: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.serialNumber}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Station #</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, building: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.building}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Placement Location</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, location: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.location}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Address 1</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, address1: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.address1}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Address 2</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, address2: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.address2}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>City</Form.Label>
                                            {
                                                equipment.fireDepartment &&
                                                <>
                                                    &nbsp;(Dept: {equipment.fireDepartment.hqcity})<br />
                                                </>
                                            }
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, city: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.city}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>State</Form.Label>
                                            {
                                                equipment.fireDepartment &&
                                                <>
                                                    &nbsp;(Dept: {equipment.fireDepartment.hqstate})<br />
                                                </>
                                            }
                                            {
                                                <StateChooser
                                                    onChange={newVal => {
                                                        setEquipment({ ...equipment, state: newVal });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    defaultValue={equipment.state}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Postal Code</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, postalCode: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.postalCode}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Country</Form.Label>
                                            {
                                                equipment.fireDepartment &&
                                                <>
                                                    &nbsp;(Dept: {equipment.fireDepartment.hqCountry})<br />
                                                </>
                                            }
                                            <CountryChooser
                                                onChange={newVal => {
                                                    setEquipment({ ...equipment, country: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                defaultValue={equipment.country}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Demo Contact Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, contactName: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.contactName}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Demo Contact Title</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, contactTitle: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.contactTitle}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Demo Contact Email</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, contactEmail: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.contactEmail}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Demo Duration</Form.Label>
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setEquipment({ ...equipment, demoDuration: parseInt(e.target.value) });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={equipment.demoDuration}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    demoDurations.map((s, index) => <option key={s} value={s}>{s} days</option>)
                                                }
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Demo Request Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, demoRequestName: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.demoRequestName}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Demo Request Email</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, demoRequestEmail: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.demoRequestEmail}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Temp Department Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, departmentName: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.departmentName}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Temp Station Number</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, stationNumber: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.stationNumber}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Shipping Notes</Form.Label>
                                        <MarkdownEditor
                                            noun="Shipping Note"
                                            onChange={newVal => {
                                                setEquipment({ ...equipment, shippingNotes: newVal });
                                                setHasFormDataChanged(true);
                                            }}
                                            value={equipment.shippingNotes || ""}
                                        />
                                    </Form.Group>
                                </Row>

                                <Row className="pb-3">
                                    <Col>
                                        <Accordion style={{ border: "1px solid #0d6efd", borderRadius: "0.375rem" }}>
                                            <Accordion.Item
                                                eventKey="0"
                                                style={{
                                                    "backgroundColor": "#eee"
                                                }}>
                                                <Accordion.Header><strong>Dates / Timeline</strong></Accordion.Header>
                                                <Accordion.Body>
                                                    <Row>
                                                        <Col>
                                                            <Row className="align-items-end">
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Initial (Created) Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, created: e.target.value });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.created ? moment(equipment.created).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>QA Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, qaCompleteDate: e.target.value });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.qaCompleteDate ? moment(equipment.qaCompleteDate).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Received Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, receivedDate: e.target.value });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.receivedDate ? moment(equipment.receivedDate).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Demo Request Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, demoRequestDate: e.target.value });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.demoRequestDate ? moment(equipment.demoRequestDate).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Demo Approval Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, demoApprovalDate: e.target.value });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.demoApprovalDate ? moment(equipment.demoApprovalDate).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Ship Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, shippedDate: e.target.value });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.shippedDate ? moment(equipment.shippedDate).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Demo Start Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, demoStartDate: e.target.value });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.demoStartDate ? moment(equipment.demoStartDate).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Demo End Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, demoEndDate: e.target.value });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.demoEndDate ? moment(equipment.demoEndDate).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                            </Row>
                                                        </Col>
                                                    </Row>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion>
                                    </Col>
                                </Row>

                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Admin Notes</Form.Label>
                                            <MarkdownEditor
                                                noun="Note"
                                                onChange={newVal => {
                                                    setEquipment({ ...equipment, notes: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={equipment.notes || ""}
                                            />
                                        </Form.Group>
                                    </Row>
                                }
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <Row className="align-items-end">
                        <Col>
                            <Card className="mb-2">
                                <Card.Body>
                                    <Card.Title>
                                        <strong>Log</strong><br />
                                    </Card.Title>
                                    {generateLog<IEquipment>(equipment)}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                }
                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <DetailFooter
                        hasFormDataChanged={hasFormDataChanged}
                        deleteAsync={deleteAsync}
                        submitFormAsync={submitFormAsync}
                    />
                }
            </>
        );
    }
    else {
        return (
            <>
                <h1>There was an issue retreiving the Demo, please try again.</h1>
            </>
        );
    }
}

export default DemosDetail;
